import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import type { OutboundMessage } from './api';
import { useOutbound, useSendOutbound } from './queries';

/**
 * Die Versandwarteschlange -- F3-07.
 *
 * **Wer hier hineinschaut, sucht das, was nicht hinausging.** "Der Kunde
 * sagt, er hat nichts bekommen" ist die haeufigste Frage an einen Service
 * Desk, und ohne diese Ansicht liesse sie sich nur in der Datenbank
 * beantworten. Deshalb steht der Fehlertext an der Zeile: Eine
 * Zustandsangabe "fehlgeschlagen" ohne Grund verschiebt die Frage nur.
 *
 * **Der Knopf stoesst denselben Lauf an wie der Hintergrunddienst.** Er
 * ersetzt ihn nicht -- ohne den Dienst bliebe die Warteschlange stehen,
 * sobald niemand hinsieht. Er ist fuer den Augenblick nach dem Einrichten
 * und fuer den, in dem jemand am Telefon wartet.
 */
export function OutboundPanel() {
  const { t, i18n } = useTranslation();
  const messages = useOutbound();
  const send = useSendOutbound();

  const queue = messages.data ?? [];

  return (
    <section className="card">
      <div className="chart-card__head">
        <h2>{t('mail.outbound')}</h2>
        <p className="chart-card__hint">{t('mail.outboundHint')}</p>
      </div>

      <div className="row">
        <Button
          variant="secondary"
          busy={send.isPending}
          onClick={() => {
            send.mutate();
          }}
        >
          {t('mail.sendNow')}
        </Button>
        {send.data ? (
          <span className="field__hint" role="status">
            {t('mail.sendResult', {
              sent: send.data.sent,
              failed: send.data.failed,
              abandoned: send.data.abandoned,
            })}
          </span>
        ) : null}
      </div>

      {send.isError ? <ErrorNotice error={send.error} /> : null}
      {messages.isPending ? <SkeletonTable columns={5} /> : null}
      {messages.isError ? <ErrorNotice error={messages.error} /> : null}

      {queue.length === 0 ? <p className="table__muted">{t('mail.outboundEmpty')}</p> : null}

      {queue.length > 0 ? (
        <div className="table-wrapper table-wrapper--tall">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('mailField.to')}</th>
                <th scope="col">{t('mailField.subject')}</th>
                <th scope="col">{t('privacy.status')}</th>
                <th scope="col">{t('mail.attempts')}</th>
                <th scope="col">{t('errors.title')}</th>
              </tr>
            </thead>
            <tbody>
              {queue.map((message) => (
                <tr key={message.id}>
                  <td className="table__muted">{message.to_addresses.join(', ')}</td>
                  <td>{message.subject}</td>
                  <td>
                    <Badge tone={toneFor(message)}>{t(`outboundStatus.${message.status}`)}</Badge>
                    {message.sent_at ? (
                      <div className="table__muted">
                        {formatDateTime(message.sent_at, i18n.language)}
                      </div>
                    ) : null}
                  </td>
                  <td className="table__number">{message.attempts}</td>
                  {/* Der Grund und nicht nur der Zustand: "fehlgeschlagen"
                      allein verschiebt die Frage nur einen Schritt weiter. */}
                  <td className="table__muted">{message.last_error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function toneFor(message: OutboundMessage): BadgeTone {
  switch (message.status) {
    case 'sent':
      return 'success';
    case 'failed':
      return 'warning';
    case 'abandoned':
      return 'danger';
    default:
      return 'neutral';
  }
}
